<script setup lang="ts">
import Dialog from '@/components/Dialog/Dialog.vue';
import { ref } from 'vue';
import RTSystemTile from '@/components/RTSystemTile/RTSystemTile.vue';
import { MedicalSystemRtSystemDto } from '@/api/generator';
import Notification from '@/components/notifications/Notification.vue';
import { NotificationMessage } from '@/components/notifications/types';
import Button from '@/components/buttons/Button.vue';

interface EntityScopeSelectionModalProps {
  selectedId?: string | null;
  options: MedicalSystemRtSystemDto[];
  modelValue?: boolean;
  title?: string;
  message?: string;
  notification?: NotificationMessage;
  showTrackItAlias?: boolean;
}

const props = defineProps<EntityScopeSelectionModalProps>();

const emits = defineEmits<{
  (e: 'submit', selectedId: string): void;
  (e: 'update:modelValue', open: boolean): void;
}>();

defineSlots<{
  notification(): void;
  innerActions(props: { selectedItem?: string }): void;
}>();

const selectedItem = ref<string | undefined>(props.selectedId || undefined);

function close(open: boolean = false) {
  selectedItem.value = props.selectedId || undefined;
  emits('update:modelValue', open);
}

function select(id: string) {
  selectedItem.value = id;
}

function submit(selectedId: string | undefined) {
  if (selectedId) {
    emits('submit', selectedId);
  }
}
</script>

<template>
  <Dialog :modelValue="modelValue" size="large" @update:modelValue="close">
    <template #title>{{ title }}</template>

    <template #text>
      <p class="text-primary-darken-2 mb-6">
        {{ message }}
      </p>

      <div class="mb-6">
        <slot name="notification" />
      </div>

      <Notification
        v-if="notification"
        :value="notification"
        :closable="false"
        class="mt-0 mb-6 pa-4"
      />

      <div class="radio-group">
        <RTSystemTile
          v-for="option in options"
          :key="option.id"
          :item="option"
          :selected="selectedItem === option.id"
          :dontShowBadges="true"
          :showTrackItAlias="showTrackItAlias"
          @click="select(option.id)"
        />
      </div>
    </template>

    <template #actions>
      <Button variant="text" :label="$t('Cancel')" @click="close()" />
      <slot name="innerActions" :selectedItem="selectedItem">
        <Button
          variant="secondary"
          :label="$t('Apply')"
          :disabled="!selectedItem"
          @click="submit(selectedItem)"
        />
      </slot>
    </template>
  </Dialog>
</template>

<style lang="css">
.radio-group {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}
</style>
